import { STUDIO_ASSETS, WHERE_THE_LAKE_ENDS } from './data';
import type { CaptureSettings, DirectionSettings, StudioCommand, StudioEventMap, StudioScene, StudioShot, StudioState } from './types';

const clone = <T>(value: T): T => structuredClone(value);

export class StudioController extends EventTarget {
  private value: StudioState;

  readonly assets = STUDIO_ASSETS;

  constructor() {
    super();
    const firstScene = WHERE_THE_LAKE_ENDS.scenes[0];
    const firstShot = firstScene.shots[0];
    this.value = {
      episode: WHERE_THE_LAKE_ENDS,
      selectedSceneId: firstScene.id,
      selectedShotId: firstShot.id,
      selectedAssetId: null,
      currentTime: 0,
      isPlaying: false,
      direction: clone(firstShot.direction),
      capture: { format: 'WebM take', resolution: '1920 × 1080', includeGuides: false },
      mobilePanel: null,
    };
  }

  get state(): Readonly<StudioState> {
    return this.value;
  }

  get scene(): StudioScene {
    return this.value.episode.scenes.find((scene) => scene.id === this.value.selectedSceneId) ?? this.value.episode.scenes[0];
  }

  get shot(): StudioShot {
    return this.scene.shots.find((shot) => shot.id === this.value.selectedShotId) ?? this.scene.shots[0];
  }

  get duration(): number {
    return this.scene.shots.reduce((total, shot) => total + shot.duration, 0);
  }

  selectScene(sceneId: string): void {
    const scene = this.value.episode.scenes.find((candidate) => candidate.id === sceneId);
    if (!scene) return;
    const shot = scene.shots[0];
    this.value = { ...this.value, selectedSceneId: scene.id, selectedShotId: shot.id, currentTime: 0, isPlaying: false, direction: clone(shot.direction) };
    this.emit({ type: 'scene-selected', sceneId });
  }

  selectShot(shotId: string): void {
    const shot = this.scene.shots.find((candidate) => candidate.id === shotId);
    if (!shot) return;
    const shotStart = this.scene.shots.slice(0, this.scene.shots.indexOf(shot)).reduce((total, candidate) => total + candidate.duration, 0);
    this.value = { ...this.value, selectedShotId: shot.id, currentTime: shotStart, isPlaying: false, direction: clone(shot.direction) };
    this.emit({ type: 'shot-selected', shotId });
  }

  play(): void { this.setPlaying(true); }
  pause(): void { this.setPlaying(false); }
  togglePlayback(): void { this.setPlaying(!this.value.isPlaying); }

  seek(time: number): void {
    const nextTime = Math.max(0, Math.min(this.duration, Number.isFinite(time) ? time : 0));
    this.value = { ...this.value, currentTime: nextTime };
    this.syncShotToPlayhead();
    this.emit({ type: 'seek', time: nextTime });
  }

  tick(deltaSeconds: number): void {
    if (!this.value.isPlaying || deltaSeconds <= 0) return;
    const next = this.value.currentTime + deltaSeconds;
    if (next >= this.duration) {
      this.value = { ...this.value, currentTime: this.duration, isPlaying: false };
      this.syncShotToPlayhead();
      this.emit({ type: 'playback', playing: false });
      return;
    }
    this.value = { ...this.value, currentTime: next };
    this.syncShotToPlayhead();
    this.notify();
  }

  updateDirection(patch: Partial<DirectionSettings>): void {
    this.value = { ...this.value, direction: { ...this.value.direction, ...patch } };
    this.emit({ type: 'direction-changed', direction: clone(this.value.direction) });
  }

  updateCapture(patch: Partial<CaptureSettings>): void {
    this.value = { ...this.value, capture: { ...this.value.capture, ...patch } };
    this.notify();
  }

  selectAsset(assetId: string): void {
    if (!this.assets.some((asset) => asset.id === assetId)) return;
    this.value = { ...this.value, selectedAssetId: assetId };
    this.emit({ type: 'asset-selected', assetId });
  }

  setMobilePanel(panel: StudioState['mobilePanel']): void {
    this.value = { ...this.value, mobilePanel: this.value.mobilePanel === panel ? null : panel };
    this.notify();
  }

  requestCapture(): void {
    this.emitCommand({ type: 'capture-frame', shotId: this.shot.id, settings: clone(this.value.capture) });
  }

  requestExport(): void {
    this.emitCommand({ type: 'export-scene', sceneId: this.scene.id, settings: clone(this.value.capture) });
  }

  on<K extends keyof StudioEventMap>(type: K, listener: (event: StudioEventMap[K]) => void): () => void {
    const wrapped = listener as EventListener;
    this.addEventListener(type, wrapped);
    return () => this.removeEventListener(type, wrapped);
  }

  private setPlaying(playing: boolean): void {
    if (playing && this.value.currentTime >= this.duration) this.value = { ...this.value, currentTime: 0 };
    this.value = { ...this.value, isPlaying: playing };
    this.emit({ type: 'playback', playing });
  }

  private syncShotToPlayhead(): void {
    let cursor = 0;
    const shot = this.scene.shots.find((candidate) => {
      cursor += candidate.duration;
      return this.value.currentTime < cursor;
    }) ?? this.scene.shots.at(-1);
    if (shot && shot.id !== this.value.selectedShotId) {
      this.value = { ...this.value, selectedShotId: shot.id, direction: clone(shot.direction) };
    }
  }

  private emit(command: StudioCommand): void {
    this.notify();
    this.emitCommand(command);
  }

  private notify(): void {
    this.dispatchEvent(new CustomEvent<StudioState>('studio:statechange', { detail: clone(this.value) }));
  }

  private emitCommand(command: StudioCommand): void {
    this.dispatchEvent(new CustomEvent<StudioCommand>('studio:command', { detail: command }));
  }
}
